import { useCallback } from 'react';
import {
  DebugService,
  DateOverrideResponse,
  JoinDateOverrideResponse,
  ClearReportsResponse,
  ExportFirestoreBackupResponse,
} from '../services/debug';

export interface DebugActions {
  clearReports: () => Promise<ClearReportsResponse>;
  getDateOverride: (forceRefresh?: boolean) => Promise<DateOverrideResponse>;
  setDateOverride: (date: Date | null) => Promise<DateOverrideResponse>;
  getJoinDateOverride: () => Promise<JoinDateOverrideResponse>;
  setJoinDateOverride: (date: Date | null) => Promise<JoinDateOverrideResponse>;
  exportFirestoreBackup: () => Promise<ExportFirestoreBackupResponse>;
  testSentryBackend: () => Promise<{ eventId: string; timestamp: string }>;
}

export const useDebugActions = (): DebugActions => {
  const clearReports = useCallback(async () => {
    try {
      return await DebugService.clearReports();
    } catch (error) {
      console.error('Error clearing reports:', error);
      throw error;
    }
  }, []);

  const getDateOverride = useCallback(
    async (forceRefresh = false): Promise<DateOverrideResponse> => {
      try {
        return await DebugService.getDateOverride(forceRefresh);
      } catch (error) {
        console.error('Error getting date override:', error);
        throw error;
      }
    },
    []
  );

  const setDateOverride = useCallback(async (date: Date | null): Promise<DateOverrideResponse> => {
    try {
      return await DebugService.setDateOverride(date);
    } catch (error) {
      console.error('Error setting date override:', error);
      throw error;
    }
  }, []);

  const getJoinDateOverride = useCallback(async (): Promise<JoinDateOverrideResponse> => {
    try {
      return await DebugService.getJoinDateOverride();
    } catch (error) {
      console.error('Error getting join date override:', error);
      throw error;
    }
  }, []);

  const setJoinDateOverride = useCallback(
    async (date: Date | null): Promise<JoinDateOverrideResponse> => {
      try {
        return await DebugService.setJoinDateOverride(date);
      } catch (error) {
        console.error('Error setting join date override:', error);
        throw error;
      }
    },
    []
  );

  const exportFirestoreBackup = useCallback(async (): Promise<ExportFirestoreBackupResponse> => {
    try {
      return await DebugService.exportFirestoreBackup();
    } catch (error) {
      console.error('Error exporting Firestore backup:', error);
      throw error;
    }
  }, []);

  const testSentryBackend = useCallback(async (): Promise<{
    eventId: string;
    timestamp: string;
  }> => {
    try {
      return await DebugService.testSentryBackend();
    } catch (error) {
      console.error('Error sending Sentry backend test:', error);
      throw error;
    }
  }, []);

  return {
    clearReports,
    getDateOverride,
    setDateOverride,
    getJoinDateOverride,
    setJoinDateOverride,
    exportFirestoreBackup,
    testSentryBackend,
  };
};
